import * as React from 'react';

import * as api from '@gitbook/api-client';
import { GITSYNC_OPERATION_TIMEOUT } from '@gitbook/app-database';
import { useNow } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { useRealtimeChannel } from '../../state';

export type GitSyncCurrentStatus =
    | {
          state: 'running';
          direction: api.GitSyncOperationDirection;
          stage: api.GitSyncOperationStage;
          progress: number;
          error?: undefined;
      }
    | {
          state: 'failure';
          direction: api.GitSyncOperationDirection;
          stage: api.GitSyncOperationStage;
          progress?: undefined;
          error: string;
      }
    | {
          state: 'timed-out';
          direction: api.GitSyncOperationDirection;
          stage: api.GitSyncOperationStage;
          progress?: undefined;
          error?: undefined;
      }
    | {
          state: 'success';
          direction: api.GitSyncOperationDirection;
          stage: api.GitSyncOperationStage;
          progress?: undefined;
          error?: undefined;
      };

function computeGitSyncProgressPercent(state: api.GitSyncOperationChannelData['data']) {
    if (!state || !state.dispatchedTasks) {
        return 0;
    }
    return (state.completedTasks / state.dispatchedTasks) * 100;
}

/**
 * Return the current status of the GitSync installation.
 */
export function useGitSyncCurrentStatus(space: api.Space): GitSyncCurrentStatus | null {
    const hasGitSyncEnabled = Boolean(space.gitSync);
    const now = useNow(hasGitSyncEnabled ? undefined : 0);

    const operation = useRealtimeChannel(
        {
            channel: 'space-gitsync-operation',
            space: space.id,
        },
        undefined,
        { skip: !hasGitSyncEnabled }
    );

    const isOperationTimeout = Boolean(
        operation && new Date(operation.updatedAt).getTime() < now - GITSYNC_OPERATION_TIMEOUT
    );

    return React.useMemo(() => {
        const progress = operation ? computeGitSyncProgressPercent(operation) : 0;

        const stage: api.GitSyncOperationStage =
            operation?.stage || api.GitSyncOperationStage.RepoFetch;

        // New data in realtime-channels
        if (operation?.state) {
            switch (operation.state) {
                case api.GitSyncOperationState.Running: {
                    if (isOperationTimeout) {
                        return {
                            state: 'timed-out',
                            direction: operation.direction,
                            stage,
                        };
                    }

                    return {
                        state: 'running',
                        direction: operation.direction,
                        progress,
                        stage,
                    };
                }
                case api.GitSyncOperationState.Failure: {
                    return {
                        state: 'failure',
                        direction: operation.direction,
                        error: operation.error ?? 'Unknown error',
                        stage,
                    };
                }
                case api.GitSyncOperationState.Success: {
                    return {
                        state: 'success',
                        direction: operation.direction,
                        stage,
                    };
                }
                default:
                    assertNever(operation.state);
            }
        }

        return null;
    }, [operation, isOperationTimeout]);
}
